import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { formatDateTime } from "../helpers";
import formatCell from "./formatCell";
import { resolveLogoDataUrl } from "./loadImage";
import loadImageAsDataUrl from "./loadImage";
import defaultLogo from "@/assets/logo-print-3.png";
import {
  INK_900,
  MUTED,
  MUTED_SOFT,
  LINE,
  SURFACE_2,
  ACCENT_600,
  ACCENT_050,
  WHITE
} from "./colors";

const MARGIN = 32;
const LOGO_MAX_W = 130;
const LOGO_MAX_H = 34;
const TOP_BAR_HEIGHT = 5;
const SIGNATURE_BLOCK_HEIGHT = 62;

function slugify(title) {
  return (title || "report")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function drawImageFit(doc, dataUrl, x, y, maxW, maxH, align) {
  if (!dataUrl) return;
  const props = doc.getImageProperties(dataUrl);
  const ratio = Math.min(maxW / props.width, maxH / props.height, 1);
  const w = props.width * ratio;
  const h = props.height * ratio;
  const drawX = align === "right" ? x + maxW - w : x;
  doc.addImage(dataUrl, props.fileType || "PNG", drawX, y, w, h);
}

function drawEyebrowValue(doc, label, value, x, y, align) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED_SOFT);
  doc.text(label.toUpperCase(), x, y, { align });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...INK_900);
  doc.text(value, x, y + 12, { align });
}

function drawHeader(doc, input, businessLogo, branchLogo, pageWidth) {
  doc.setFillColor(...ACCENT_600);
  doc.rect(0, 0, pageWidth, TOP_BAR_HEIGHT, "F");

  let y = MARGIN;
  const showBranchLogo = businessLogo && branchLogo;

  if (businessLogo) {
    drawImageFit(doc, businessLogo, MARGIN, y, LOGO_MAX_W, LOGO_MAX_H, "left");
  }
  if (showBranchLogo) {
    drawImageFit(
      doc,
      branchLogo,
      pageWidth - MARGIN - LOGO_MAX_W,
      y,
      LOGO_MAX_W,
      LOGO_MAX_H,
      "right"
    );
  }
  y += LOGO_MAX_H + 12;

  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.75);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 22;

  doc.setTextColor(...INK_900);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.text(input.title || "Report", pageWidth / 2, y, { align: "center" });
  y += 16;

  if (input.subtitle) {
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(input.subtitle, pageWidth / 2, y, { align: "center" });
    y += 14;
  }

  y += 10;

  const metaY = y;
  const hasDateRange =
    input.dateRange && (input.dateRange.start || input.dateRange.end);
  if (hasDateRange) {
    drawEyebrowValue(
      doc,
      "Report Period",
      `${input.dateRange.start || ""} — ${input.dateRange.end || ""}`,
      MARGIN,
      metaY,
      "left"
    );
  }
  drawEyebrowValue(
    doc,
    "Printed On",
    formatDateTime(new Date()),
    pageWidth - MARGIN,
    metaY,
    "right"
  );
  y = metaY + 20;

  if (input.filters && input.filters.length) {
    const filterLine = input.filters
      .filter(f => f.value)
      .map(f => `${f.label}: ${f.value}`)
      .join("   |   ");
    if (filterLine) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8.5);
      doc.setTextColor(...MUTED_SOFT);
      doc.text(filterLine, MARGIN, y);
      y += 14;
    }
  }

  return y + 8;
}

function drawFooter(doc, pageWidth, pageHeight) {
  const y = pageHeight - MARGIN + 8;
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y - 12, pageWidth - MARGIN, y - 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED_SOFT);
  const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
  const pageCount = doc.internal.getNumberOfPages();
  doc.text(`Page ${pageNumber} of ${pageCount}`, pageWidth - MARGIN, y, {
    align: "right"
  });
}

function drawSummary(doc, summary, pageWidth, startY) {
  const boxWidth = 220;
  const rowHeight = 16;
  const x = pageWidth - MARGIN - boxWidth;
  let y = startY + 14;

  summary.forEach((item, idx) => {
    const isLast = idx === summary.length - 1;
    if (isLast) {
      doc.setFillColor(...ACCENT_050);
      doc.rect(x, y - 11, boxWidth, rowHeight, "F");
      doc.setDrawColor(...ACCENT_600);
      doc.setLineWidth(2);
      doc.line(x, y - 11, x, y - 11 + rowHeight);
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(item.label, x + 8, y);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...INK_900);
    doc.text(String(item.value), x + boxWidth - 8, y, { align: "right" });
    y += rowHeight;
  });

  return y;
}

function drawSignatureBlock(doc, pageWidth, startY, printedBy) {
  const gap = 30;
  const available = pageWidth - 2 * MARGIN;
  const blockWidth = (available - gap * 2) / 3;
  const valueY = startY + 20;
  const lineY = valueY + 8;

  const items = [
    { label: "Printed By", value: printedBy || "-" },
    { label: "Checked By", value: "-" },
    { label: "Approved By", value: "-" }
  ];

  items.forEach((item, idx) => {
    const x = MARGIN + idx * (blockWidth + gap);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...INK_900);
    doc.text(item.value, x, valueY);

    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.75);
    doc.line(x, lineY, x + blockWidth, lineY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    doc.text(item.label.toUpperCase(), x, lineY + 12);
  });

  return startY + SIGNATURE_BLOCK_HEIGHT;
}

export default async function exportReportPdf(input) {
  const cols = (input.cols || []).filter(c => c.column !== "action");
  const orientation = cols.length > 7 ? "landscape" : "portrait";
  const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const [businessLogo, branchLogo] = await Promise.all([
    resolveLogoDataUrl(input.businessLogoUrl, defaultLogo),
    loadImageAsDataUrl(input.branchLogoUrl)
  ]);

  const startY = drawHeader(doc, input, businessLogo, branchLogo, pageWidth);
  const drawPageFooter = () => drawFooter(doc, pageWidth, pageHeight);

  doc.autoTable({
    startY,
    head: [cols.map(c => c.label)],
    body: (input.rows || []).map((row, rIdx) =>
      cols.map(c => formatCell(row, c, rIdx))
    ),
    theme: "grid",
    showHead: "everyPage",
    margin: { top: MARGIN, left: MARGIN, right: MARGIN, bottom: MARGIN + 10 },
    styles: {
      fontSize: 8,
      cellPadding: 4,
      textColor: INK_900,
      lineColor: LINE,
      lineWidth: 0.5
    },
    headStyles: {
      fillColor: ACCENT_600,
      textColor: WHITE,
      fontStyle: "bold",
      lineColor: ACCENT_600
    },
    alternateRowStyles: {
      fillColor: SURFACE_2
    },
    columnStyles: cols.reduce((acc, c, idx) => {
      if (c.formatAmount || c.sum) acc[idx] = { halign: "right" };
      return acc;
    }, {}),
    didDrawPage: drawPageFooter
  });

  let afterTableY = doc.lastAutoTable.finalY;

  const hasSummary = input.summary && input.summary.length;
  const summaryHeight = hasSummary ? 14 + input.summary.length * 16 : 0;
  const totalTrailingHeight =
    summaryHeight + (hasSummary ? 20 : 14) + SIGNATURE_BLOCK_HEIGHT;
  const spaceLeft = pageHeight - MARGIN - afterTableY;

  if (spaceLeft < totalTrailingHeight) {
    doc.addPage();
    drawPageFooter();
    afterTableY = MARGIN;
  }

  if (hasSummary) {
    afterTableY = drawSummary(doc, input.summary, pageWidth, afterTableY);
  }

  drawSignatureBlock(doc, pageWidth, afterTableY + 10, input.printedBy);

  doc.save(`${input.fileName || slugify(input.title)}.pdf`);
}
